import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SfAccountSnapshot } from '@/types';
import { api } from '@/lib/api';
import { Loader2, Settings, AlertTriangle } from 'lucide-react';

interface ChangeSubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: SfAccountSnapshot;
  onSuccess: () => void;
  currentAllowance?: number;
}

const SUBSCRIPTION_TIERS = [
  { value: 50, label: '50 minutes' },
  { value: 100, label: '100 minutes' },
  { value: 200, label: '200 minutes' },
  { value: 500, label: '500 minutes' },
] as const;

function formatDateForDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function ChangeSubscriptionModal({
  open,
  onOpenChange,
  account,
  onSuccess,
  currentAllowance,
}: ChangeSubscriptionModalProps) {
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);

  // Filter out the current subscription tier from available options
  const availableTiers = SUBSCRIPTION_TIERS.filter(tier => tier.value !== currentAllowance);

  const handleSubmit = async () => {
    if (!selectedTier) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const result = await api.changeSubscription(account.sf_account_id, parseInt(selectedTier, 10));

      if (result.success) {
        setSuccessMessage(result.message);
        setNextBillingDate(result.next_billing_date);
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
          setNextBillingDate(null);
          onSuccess();
        }, 3000);
      } else {
        setError(result.error || 'Failed to change subscription');
      }
    } catch (err: unknown) {
      const apiError = err as { error?: string };
      const errorMessage = apiError?.error || (err instanceof Error ? err.message : 'An unexpected error occurred');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setSuccess(false);
      setSuccessMessage('');
      setNextBillingDate(null);
      setSelectedTier('');
      onOpenChange(false);
    }
  };

  const selectedTierLabel = SUBSCRIPTION_TIERS.find(t => t.value === parseInt(selectedTier, 10))?.label;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-abby-primary" />
            Change Subscription Level
          </DialogTitle>
          <DialogDescription>
            Select a new subscription level for {account.account_name}. This change takes effect on your next billing date.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-gray-600">{successMessage}</p>
            {nextBillingDate && (
              <p className="text-sm text-gray-500 mt-1">
                Effective: {formatDateForDisplay(nextBillingDate)}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="subscription-tier">New Subscription Level</Label>
                <Select
                  value={selectedTier}
                  onValueChange={setSelectedTier}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="subscription-tier">
                    <SelectValue placeholder="Select a subscription level" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTiers.map((tier) => (
                      <SelectItem key={tier.value} value={String(tier.value)}>
                        {tier.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  The new level will apply starting from your next billing cycle.
                </p>
              </div>

              {selectedTier && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-800">Confirm subscription change</p>
                      <p className="text-blue-700 mt-1">
                        The subscription will change to <strong>{selectedTierLabel}</strong> on your next billing date.
                        This does not affect the current billing cycle.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !selectedTier}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Schedule Change'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
